import React, { useState, useEffect, useCallback } from 'react';
import { Feature, FeatureCreate, FeatureUpdate } from '../types';
import featureService from '../services/featureService';
import Modal from './Modal';

interface FeatureManagerProps {
  onClose: () => void;
}

interface FeatureFormProps {
  feature?: Feature;
  onSubmit: (feature: FeatureCreate | FeatureUpdate) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const FeatureForm: React.FC<FeatureFormProps> = ({ feature, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState<FeatureCreate>({
    feature_name: feature?.feature_name || '',
    display_name: feature?.display_name || '',
    description: feature?.description || '',
    category: feature?.category || '',
    is_active: feature?.is_active !== undefined ? feature.is_active : true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.feature_name.trim()) {
      newErrors.feature_name = 'Feature name is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.feature_name)) {
      newErrors.feature_name = 'Feature name must contain only lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    if (feature) {
      // For updates, exclude feature_name since it can't be changed
      const { feature_name, ...updateData } = formData;
      await onSubmit(updateData as FeatureUpdate);
    } else {
      // For creates, include all data
      await onSubmit(formData);
    }
  };

  const categories = [
    'document', 'audit', 'form', 'module', 'archive', 'numbering', 
    'lifecycle', 'reporting', 'tagging', 'billing', 'compliance', 
    'history', 'relationship'
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Feature Name *</label>
        <input
          type="text"
          name="feature_name"
          value={formData.feature_name}
          onChange={handleChange}
          className={`form-control ${errors.feature_name ? 'error' : ''}`}
          placeholder="e.g., doc-generator-pdf"
          disabled={!!feature} // Don't allow editing feature_name for existing features
        />
        {errors.feature_name && <div className="form-error">{errors.feature_name}</div>}
        <small className="form-help">
          Use lowercase letters, numbers, and hyphens only. This cannot be changed after creation.
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Display Name</label>
        <input
          type="text"
          name="display_name"
          value={formData.display_name}
          onChange={handleChange}
          className="form-control"
          placeholder="e.g., PDF Document Generator"
        />
        <small className="form-help">
          Human-readable name for the feature
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Category</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="form-control"
        >
          <option value="">Select a category</option>
          {categories.map(category => (
            <option key={category} value={category}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows={3}
          placeholder="Describe what this feature does..."
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            style={{ marginRight: '8px' }}
          />
          Active
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : (feature ? 'Update' : 'Create')}
        </button>
      </div>
    </form>
  );
};

const FeatureManager: React.FC<FeatureManagerProps> = ({ onClose }) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  
  useEffect(() => {
    loadCategories();
  }, []);

  const loadFeatures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.getFeatures({
        limit: 1000, // Load all features
        category: filterCategory || undefined,
        is_active: filterActive !== null ? filterActive : undefined
      });
      setFeatures(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterActive]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const loadCategories = async () => {
    try {
      const data = await featureService.getFeatureCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleCreateFeature = async (featureData: FeatureCreate | FeatureUpdate) => {
    try {
      setFormLoading(true);
      await featureService.createFeature(featureData as FeatureCreate);
      setShowForm(false);
      await loadFeatures();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create feature');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateFeature = async (featureData: FeatureCreate | FeatureUpdate) => {
    if (!editingFeature) return;

    try {
      setFormLoading(true);
      await featureService.updateFeature(editingFeature.feature_id, featureData as FeatureUpdate);
      setEditingFeature(null);
      setShowForm(false);
      await loadFeatures();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update feature');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteFeature = async (feature: Feature) => {
    if (!window.confirm(`Are you sure you want to delete "${feature.display_name || feature.feature_name}"?`)) {
      return;
    }

    try {
      await featureService.deleteFeature(feature.feature_id);
      await loadFeatures();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete feature');
    }
  };

  const handleEditFeature = (feature: Feature) => {
    setEditingFeature(feature);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingFeature(null);
  };

  const filteredFeatures = features.filter(feature => {
    const matchesSearch = !searchTerm || 
      feature.feature_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (showForm) {
    return (
      <Modal 
        title={editingFeature ? 'Edit Feature' : 'Create New Feature'} 
        onClose={handleCloseForm}
      >
        <FeatureForm
          feature={editingFeature || undefined}
          onSubmit={editingFeature ? handleUpdateFeature : handleCreateFeature}
          onCancel={handleCloseForm}
          loading={formLoading}
        />
      </Modal>
    );
  }

  return (
    <Modal title="Feature Management" onClose={onClose}>
      <div className="feature-manager">
        {error && (
          <div className="alert alert-error">
            <p>{error}</p>
            <button onClick={() => setError(null)} className="btn btn-sm">×</button>
          </div>
        )}

        {/* Header Actions */}
        <div className="manager-header">
          <button 
            onClick={() => setShowForm(true)} 
            className="btn btn-primary"
          >
            Create New Feature
          </button>
          <button 
            onClick={loadFeatures} 
            className="btn btn-secondary"
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search features..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                // Reload features when filter changes
                setTimeout(loadFeatures, 100);
              }}
              className="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <select
              value={filterActive === null ? '' : filterActive.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setFilterActive(value === '' ? null : value === 'true');
                // Reload features when filter changes
                setTimeout(loadFeatures, 100);
              }}
              className="status-filter"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {/* Features Table */}
        <div className="features-table-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading features...</p>
            </div>
          ) : (
            <table className="features-table">
              <thead>
                <tr>
                  <th>Feature Name</th>
                  <th>Display Name</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeatures.map(feature => (
                  <tr key={feature.feature_id}>
                    <td>
                      <code className="feature-name">{feature.feature_name}</code>
                    </td>
                    <td>{feature.display_name || '-'}</td>
                    <td>
                      {feature.category && (
                        <span className={`category-badge category-${feature.category}`}>
                          {feature.category}
                        </span>
                      )}
                    </td>
                    <td className="description-cell">
                      {feature.description ? (
                        <span title={feature.description}>
                          {feature.description.length > 50 
                            ? `${feature.description.substring(0, 50)}...`
                            : feature.description
                          }
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${feature.is_active ? 'active' : 'inactive'}`}>
                        {feature.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {new Date(feature.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEditFeature(feature)}
                          className="btn btn-sm btn-secondary"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteFeature(feature)}
                          className="btn btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredFeatures.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="empty-state">
                      No features found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .feature-manager {
          max-height: 80vh;
          overflow-y: auto;
        }

        .manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          align-items: center;
        }

        .filter-group {
          flex: 1;
        }

        .search-input, .category-filter, .status-filter {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .features-table-container {
          max-height: 500px;
          overflow-y: auto;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .features-table {
          width: 100%;
          border-collapse: collapse;
        }

        .features-table th,
        .features-table td {
          padding: 0.75rem;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }

        .features-table th {
          background-color: #f8f9fa;
          font-weight: 500;
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .feature-name {
          background-color: #f8f9fa;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.875rem;
        }

        .category-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-badge.active {
          background-color: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background-color: #f8d7da;
          color: #721c24;
        }

        .description-cell {
          max-width: 200px;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #6c757d;
        }

        .loading-container {
          text-align: center;
          padding: 2rem;
        }

        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Category colors */
        .category-document { background-color: #e3f2fd; color: #1976d2; }
        .category-audit { background-color: #f3e5f5; color: #7b1fa2; }
        .category-form { background-color: #e8f5e8; color: #388e3c; }
        .category-module { background-color: #fff3e0; color: #f57c00; }
        .category-archive { background-color: #fce4ec; color: #c2185b; }
        .category-numbering { background-color: #e0f2f1; color: #00796b; }
        .category-lifecycle { background-color: #f1f8e9; color: #689f38; }
        .category-reporting { background-color: #e8eaf6; color: #3f51b5; }
        .category-tagging { background-color: #fff8e1; color: #ffa000; }
        .category-billing { background-color: #ffebee; color: #d32f2f; }
        .category-compliance { background-color: #f9fbe7; color: #827717; }
        .category-history { background-color: #efebe9; color: #5d4037; }
        .category-relationship { background-color: #fafafa; color: #424242; }
        `
      }} />
    </Modal>
  );
};

export default FeatureManager;